/**
 * 집 구조 기록 완료 화면 — v2 문서 버전.
 *
 * 프로덕션에서는 `HouseStructureFlowAdapter.makeDoneScreen` 이 만드는 커스텀 터미널이다.
 * 방 목록 확인 → "수정이 필요해요" → 방/태그 삭제·방 추가(바텀시트 2단) → "확인" 의 흐름
 * 전체가 React state 로 구현돼 있어 편집자가 손댈 수 없었다.
 *
 * 이 문서는 같은 흐름을 스텝·시트·액션으로 옮긴 것이다. v1 FlowDocument 로는 표현이
 * 불가능했던 부분이 여기 다 들어 있다:
 *   - 같은 데이터의 두 가지 모드(보기/수정) → 스텝 2개
 *   - 목록에서 항목·하위 태그 제거 → `removeWhere` / `removeFrom` 액션
 *   - 2단 바텀시트(타입 선택 → 직접입력) → `sheets` + 조건부 전이
 *
 * **화면은 원본을 그대로 따른다.** 흰 배경 위 24px 굵은 제목 두 줄, 밑줄로만 나뉘는 방
 * 목록, 파란 태그, 점선 "+ 방 추가하기", 하단의 주 버튼 + 텍스트 버튼. 시트가 열리면
 * 하단 CTA 를 감추는 규칙까지 같다(그건 문서가 아니라 셸이 안다).
 */
import type { AssetDocument } from "@miso/design-system";

/** 프로덕션 `ROOM_TYPE_OPTIONS` 과 같은 목록 — 라벨이 곧 값이다. */
const ROOM_TYPE_OPTIONS = [
  { value: "현관", label: "현관" },
  { value: "방", label: "방" },
  { value: "거실", label: "거실" },
  { value: "화장실", label: "화장실" },
  { value: "세대창고", label: "세대창고" },
  { value: "베란다", label: "베란다" },
  { value: "other", label: "그 외" },
];

/** 호스트가 등록해야 하는 이름. */
export const HOUSE_STRUCTURE_REFS = {
  /** transform 출력 → 이름 붙은 방 목록. 순수 변환이라 이 패키지가 구현을 갖는다. */
  rooms: "houseStructure.rooms",
} as const;

const doc: AssetDocument = {
  id: "house-structure.done",
  schemaVersion: 2,
  initial: "entry",

  /*
   * 구조 기록 문서의 transform 이 낸 `{type,index,metadata}` 배열을 사람이 읽는 목록으로
   * 바꾼다. 이 화면이 체인으로 열릴 때 앞 문서의 값이 그대로 넘어오므로, 변환도 화면
   * 안이 아니라 화면 앞에서 일어나야 한다.
   */
  data: [
    {
      id: "structureRooms",
      derive: HOUSE_STRUCTURE_REFS.rooms,
      args: { raw: { $var: "rooms" } },
    },
  ],

  steps: {
    /*
     * 어느 문으로 들어왔는가.
     *
     * 같은 문서가 두 자리에서 열린다 — 구조 기록 직후(보기부터)와 방 선택 화면의 "방
     * 수정하기"(수정부터). 문서를 두 벌로 나누면 방 목록·시트가 두 벌이 된다.
     */
    entry: {
      kind: "transform",
      code: "return {};",
      outputKey: "_entry",
      next: {
        kind: "branch",
        when: [{ if: { "==": [{ var: "__enter" }, "edit"] }, then: "edit" }],
        else: "view",
      },
    },

    // ── 보기 ────────────────────────────────────────────────────────────────
    view: {
      kind: "screen",
      layout: {
        type: "Screen",
        props: { variant: "plain", title: "방 구조가\n완성되었어요!" },
        slots: {
          footer: [
            {
              type: "CtaButton",
              props: {
                label: "다음",
                busy: { $var: "submitting" },
                busyLabel: "저장 중...",
              },
              on: {
                onPress: [
                  { do: "setValues", values: { submitting: true } },
                  /*
                   * 여기서 스케치 세션이 열린다. 방 목록이 서버로 넘어가고 방마다
                   * 옵저빙 세션이 생겨야 다음 단계(짐 기록)가 성립한다.
                   */
                  {
                    do: "callAction",
                    name: "startHouseSketchSession",
                    args: { rooms: { $var: "structureRooms" } },
                  },
                  { do: "submit" },
                ],
              },
            },
            {
              type: "CtaButton",
              props: { label: "수정이 필요해요", variant: "text", size: "s" },
              on: { onPress: [{ do: "goto", step: "edit" }] },
            },
          ],
        },
        children: [
          {
            type: "PlainRow",
            props: { label: { $var: "room.label" }, tags: { $var: "room.tags" } },
            repeat: { for: { $var: "structureRooms" }, as: "room", key: "id" },
          },
        ],
      },
      next: { kind: "exit" },
    },

    // ── 수정 ────────────────────────────────────────────────────────────────
    edit: {
      kind: "screen",
      layout: {
        type: "Screen",
        props: { variant: "plain", title: "방 구조를\n수정해주세요" },
        slots: {
          footer: [
            {
              type: "CtaButton",
              props: { label: "확인" },
              on: {
                onPress: [
                  {
                    do: "callAction",
                    name: "houseStructure.saveEdits",
                    args: { rooms: { $var: "structureRooms" } },
                  },
                  /*
                   * 수정으로 곧장 들어왔으면(방 선택 화면의 "방 수정하기") 확인이 곧
                   * 끝이다. 보기 화면으로 떨어뜨리면 이미 지나온 "다음" 버튼이 다시
                   * 나와 스케치 세션을 두 번 열게 된다.
                   */
                  {
                    do: "branch",
                    cases: [
                      {
                        when: { "==": [{ var: "__enter" }, "edit"] },
                        then: [{ do: "submit" }],
                      },
                      { then: [{ do: "goto", step: "view" }] },
                    ],
                  },
                ],
              },
            },
          ],
        },
        children: [
          {
            type: "PlainRow",
            props: { label: { $var: "room.label" }, tags: { $var: "room.tags" } },
            repeat: { for: { $var: "structureRooms" }, as: "room", key: "id" },
            on: {
              onRemove: [
                {
                  do: "removeWhere",
                  list: "structureRooms",
                  match: { id: { $var: "room.id" } },
                },
              ],
              onRemoveTag: [
                {
                  do: "removeFrom",
                  list: "structureRooms",
                  where: { id: { $var: "room.id" } },
                  field: "tags",
                  value: { $var: "$event" },
                },
              ],
            },
          },
          {
            type: "AddButton",
            props: { label: "+ 방 추가하기" },
            on: {
              onPress: [
                { do: "setValues", values: { pickedType: null } },
                { do: "openSheet", sheet: "roomType" },
              ],
            },
          },
        ],
      },
      next: { kind: "exit" },
    },
  },

  /**
   * 방 추가 2단 시트.
   *
   * 고르는 즉시 추가하지 않고 "추가하기" 로 확정한다 — 프로덕션과 같다. "그 외" 를
   * 고르면 이름 시트로 넘어간다(`confirmType` 의 `selectedType === 'other'` 분기).
   */
  sheets: {
    roomType: {
      title: "방 타입 선택",
      variant: "floating",
      layout: {
        type: "Group",
        children: [
          { type: "SheetTitle", props: { text: "어떤 방을 추가하시겠어요?" } },
          {
            type: "Group",
            children: [
              {
                type: "ChoiceRow",
                props: {
                  label: { $var: "option.label" },
                  selected: {
                    $expr: { "==": [{ var: "pickedType" }, { var: "option.value" }] },
                  },
                },
                repeat: { for: ROOM_TYPE_OPTIONS, as: "option", key: "value" },
                on: {
                  onPress: [
                    {
                      do: "setValues",
                      values: {
                        pickedType: { $var: "option.value" },
                        pickedLabel: { $var: "option.label" },
                      },
                    },
                  ],
                },
              },
            ],
          },
          {
            type: "CtaButton",
            props: {
              label: "추가하기",
              disabled: { $expr: { "==": [{ var: "pickedType" }, null] } },
            },
            on: {
              onPress: [
                {
                  do: "branch",
                  cases: [
                    {
                      when: { "==": [{ var: "pickedType" }, "other"] },
                      then: [
                        { do: "closeSheet" },
                        { do: "setValues", values: { customName: "" } },
                        { do: "openSheet", sheet: "roomName" },
                      ],
                    },
                    {
                      then: [
                        {
                          do: "appendTo",
                          list: "structureRooms",
                          keyField: "id",
                          item: { label: { $var: "pickedLabel" }, tags: [] },
                        },
                        { do: "closeSheet" },
                      ],
                    },
                  ],
                },
              ],
            },
          },
        ],
      },
    },

    roomName: {
      title: "방 이름 입력",
      variant: "floating",
      layout: {
        type: "Group",
        children: [
          { type: "SheetTitle", props: { text: "방 이름을 알려주세요" } },
          {
            type: "TextField",
            bind: "customName",
            props: {
              placeholder: "방 이름",
              label: "방 이름",
              maxLength: 20,
              autoFocus: true,
            },
          },
          {
            type: "CtaButton",
            props: {
              label: "추가하기",
              disabled: { $expr: { "==": [{ var: "customName" }, ""] } },
            },
            on: {
              onPress: [
                {
                  do: "appendTo",
                  list: "structureRooms",
                  keyField: "id",
                  item: { label: { $var: "customName" }, tags: [] },
                },
                { do: "closeSheet" },
              ],
            },
          },
        ],
      },
    },
  },
};

/** 프로덕션 transform 이 만드는 방 목록과 같은 모양의 표본. */
const initialValues: Record<string, unknown> = {
  submitting: false,
  pickedType: null,
  rooms: [
    { id: "entrance-1", label: "현관", tags: [] },
    { id: "living-1", label: "거실", tags: [] },
    { id: "room-1", label: "1번 방", tags: ["화장실", "베란다"] },
    { id: "room-2", label: "2번 방", tags: ["베란다"] },
    { id: "room-3", label: "3번 방", tags: [] },
    { id: "storage-1", label: "세대창고", tags: [] },
    { id: "other-1", label: "다락방", tags: [] },
  ],
};

export const HOUSE_STRUCTURE_DONE = { doc, initialValues };
